import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Box, Paper, Typography, TextField, Divider, Chip, List, ListItem, ListItemText, Autocomplete, Popover, Button, ButtonGroup, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import HeadsetMicOutlinedIcon from '@mui/icons-material/HeadsetMicOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleIcon from '@mui/icons-material/People';
import ListAltIcon from '@mui/icons-material/ListAlt';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CalendarPanel from './CalendarPanel';
import ItineraryModal from './ItineraryModal';
import EditBookingModal from './EditBookingModal';
import { API_BASE_URL } from '../config';
import { authenticatedFetch, apiGet, apiPost, apiPut, apiDelete } from '../utils/api';
import { authaltFetch, AUTHALT_API_BASE } from '../services/authaltApi';

// API Configuration - token fetched dynamically from backend
const GUEST_API_BASE = AUTHALT_API_BASE;
  const ViewButton = styled(Button)(({ selected }) => ({
    textTransform: 'none',
    minWidth: '120px',
    backgroundColor: selected ? '#E4C984' : 'transparent',
    color: '#171717',
    '&:hover': { backgroundColor: selected ? '#cbb16f' : 'rgba(0,0,0,0.04)' },
    borderColor: '#E4C984',
    fontWeight: 700
  }));

  const GoldButtonGroup = styled(ButtonGroup)(() => ({
    '& .MuiButton-outlined': { borderColor: '#E4C984' },
    '& .MuiButton-outlined:hover': { borderColor: '#cbb16f', backgroundColor: 'rgba(228, 201, 132, 0.12)' },
    '& .MuiButtonBase-root.Mui-focusVisible': { boxShadow: '0 0 0 2px rgba(228, 201, 132, 0.6)' }
  }));


const formatDateISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const addDaysISO = (iso, offset) => {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + offset);
  return formatDateISO(dt);
};

const formatDisplayDateRange = (days) => {
  if (!days.length) return '';
  const parseLocal = (iso) => {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d);
  };
  const first = parseLocal(days[0]);
  const last = parseLocal(days[days.length - 1]);
  const fmt = (dt, withYear) =>
    dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', ...(withYear ? { year: 'numeric' } : {}) });
  if (days.length === 1) return fmt(first, true);
  return `${fmt(first, false)} - ${fmt(last, true)}`;
};

const formatLocalDayLabel = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' });
};

const parseLocalISO = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const parseMinutes = (hhmm) => {
  if (!hhmm) return null;
  const [h, m] = String(hhmm).split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

const bookingIncludesTherapist = (booking, therapistName) => {
  if (!booking || !therapistName) return false;
  const target = String(therapistName).trim().toLowerCase();
  const primary = String(booking.therapist || booking.therapist_name || '').trim().toLowerCase();
  if (primary && primary === target) return true;
  return (booking.therapists || []).some(
    (t) => String(t?.name || '').trim().toLowerCase() === target
  );
};

const getRosteredTherapistNames = (rosterDay) => {
  const names = new Set();
  Object.values(rosterDay || {}).forEach((shiftData) => {
    (shiftData?.therapists || []).forEach((name) => {
      if (name) names.add(name);
    });
  });
  return names;
};

const getTherapistBookingAt = (therapistName, date, bookingsByDate, atMinutes) => {
  const dayBookings = bookingsByDate[date] || [];
  return dayBookings.find((booking) => {
    if (!bookingIncludesTherapist(booking, therapistName)) return false;
    const start = parseMinutes(booking.startTime || booking.start_time);
    const end = parseMinutes(booking.endTime || booking.end_time);
    if (start == null || end == null || atMinutes == null) return false;
    return atMinutes >= start && atMinutes < end;
  }) || null;
};

const ReceptionDesk = () => {
  // View switching: Desk (list/search) vs Timeline (read-only grid)
  const [activeView, setActiveView] = useState('desk');

  // Selected date for timeline
  const [date, setDate] = useState(() => formatDateISO(new Date()));
  const [guestQuery, setGuestQuery] = useState('');
  const [partnerQuery, setPartnerQuery] = useState('');
  const [roomQuery, setRoomQuery] = useState('');
  const [selectedService, setSelectedService] = useState(null); // treatment/activity filter
  const [selectedTherapist, setSelectedTherapist] = useState(null); // therapist/resource filter
  const [loading, setLoading] = useState(false);
  const [bookingsByDate, setBookingsByDate] = useState({}); // { 'YYYY-MM-DD': Booking[] }
  const [rosterByDate, setRosterByDate] = useState({}); // { 'YYYY-MM-DD': roster payload }
  const [config, setConfig] = useState({ rooms: {}, treatments: [], therapists: {}, equipment: {} });
  const [calendarDay, setCalendarDay] = useState(() => formatDateISO(new Date()));
  const [guests, setGuests] = useState([]);
  const [allGuests, setAllGuests] = useState([]); // All fetched guests
  const [apiCounts, setApiCounts] = useState({ inHouse: 0, upcoming: 0 }); // Store API-provided counts
  const [guestFilter, setGuestFilter] = useState(() => {
    // Force default to 'inhouse' - migrate from old 'upcoming' default
    const saved = localStorage.getItem('receptionDeskGuestFilter');
    if (!saved) {
      localStorage.setItem('receptionDeskGuestFilter', 'inhouse');
      return 'inhouse';
    }
    return saved;
  });
  const [guestLookupReady, setGuestLookupReady] = useState(false);
  const [dayFilter, setDayFilter] = useState('today'); // 'today', 'tomorrow', 'next7days', 'all'
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [availableActivitiesByDate, setAvailableActivitiesByDate] = useState({});
  const [showAvailableActivitiesModal, setShowAvailableActivitiesModal] = useState(false);
  const [selectedSlotInfo, setSelectedSlotInfo] = useState(null); // { date, time, activities }
  const [activityDurations, setActivityDurations] = useState({}); // Track duration for each activity by ID
  const [showItinerary, setShowItinerary] = useState(false);
  
  // Edit booking modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  useEffect(() => {
    const syncCalendarDay = () => {
      const today = formatDateISO(new Date());
      setCalendarDay((prev) => (prev === today ? prev : today));
    };
    syncCalendarDay();
    const intervalId = window.setInterval(syncCalendarDay, 60_000);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') syncCalendarDay();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  // Helper functions for guest filtering
  const getFirstNonEmpty = (obj, keys) => {
    for (const k of keys) {
      const v = obj[k];
      if (v !== null && v !== undefined && String(v).trim() !== '') return v;
    }
    return null;
  };

  const toISO = (val) => {
    if (!val) return null;
    const str = String(val).trim();
    if (!str) return null;
    if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.split('T')[0];
    const m1 = str.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})$/);
    if (m1) {
      const d = String(m1[1]).padStart(2, '0');
      const mo = String(m1[2]).padStart(2, '0');
      const y = String(m1[3]).length === 2 ? `20${m1[3]}` : String(m1[3]);
      return `${y}-${mo}-${d}`;
    }
    const t = Date.parse(str);
    if (!Number.isNaN(t)) {
      const dt = new Date(t);
      return formatDateISO(dt);
    }
    return null;
  };

  const getCheckInISO = (g) => toISO(getFirstNonEmpty(g, ['checkIn', 'checkin', 'check_in', 'startDate', 'start', 'arrivalDate', 'ArrivalDate']));
  const getCheckOutISO = (g) => toISO(getFirstNonEmpty(g, ['checkOut', 'checkout', 'check_out', 'endDate', 'end', 'departureDate', 'DepartureDate']));

  const formatDateRange = (g) => {
    const ci = getCheckInISO(g);
    const co = getCheckOutISO(g);
    if (!ci || !co) return '';
    
    const formatDate = (isoDate) => {
      const date = new Date(isoDate);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };
    
    return `${formatDate(ci)} - ${formatDate(co)}`;
  };

  const isInHouse = (g) => {
    const ci = getCheckInISO(g);
    const co = getCheckOutISO(g);
    if (!ci || !co) return false;
    return ci <= calendarDay && calendarDay <= co;
  };

  const isUpcoming = (g) => {
    const ci = getCheckInISO(g);
    if (!ci) return false;
    return ci > calendarDay;
  };

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const res = await apiGet('/api/config');
        const data = await res.json();
        setConfig({
          rooms: data.rooms || {},
          treatments: data.treatments || [],
          therapists: data.therapists || {},
          equipment: data.equipment || {}
        });
      } catch {}
    };
    loadConfig();
  }, []);

  // One-time migration: Reset filter to 'inhouse' as new default
  useEffect(() => {
    const migrated = localStorage.getItem('receptionDeskFilterMigrated');
    if (!migrated) {
      // First time after update - set to 'inhouse'
      localStorage.setItem('receptionDeskGuestFilter', 'inhouse');
      localStorage.setItem('receptionDeskFilterMigrated', 'true');
      setGuestFilter('inhouse');
    }
  }, []); // Run once on mount

  // Load guests for autocomplete lookup
  useEffect(() => {
    const fetchGuests = async () => {
      try {
        // Fetch all guest types using the new API endpoints with POST
        const types = ['InHouse', 'TodaysArrival', 'TodaysDeparture', 'Next4Days'];
        let allGuestsData = [];
        let counts = { inHouse: 0, todaysArrival: 0, todaysDeparture: 0, arrivalNext4Days: 0 };
        
        for (const type of types) {
          try {
            const res = await authaltFetch('/guestlistdatav3', {
              type,
              index: 'atmantan'
            });
            const response = await res.json();
            const categoryKey = type === 'InHouse' ? 'inHouse' :
                               type === 'TodaysArrival' ? 'todaysArrival' :
                               type === 'TodaysDeparture' ? 'todaysDeparture' : 'arrivalNext4Days';
            const guestsFromType = response[categoryKey]?.data || [];
            counts[categoryKey] = response[categoryKey]?.count || 0;
            allGuestsData = allGuestsData.concat(guestsFromType);
          } catch (e) {
            console.error(`Failed to fetch ${type}:`, e);
          }
        }
        
        // Store API counts
        setApiCounts({
          inHouse: counts.inHouse,
          upcoming: counts.todaysArrival + counts.arrivalNext4Days
        });
        
        // Remove duplicates by guestId
        const uniqueGuests = Array.from(
          new Map(allGuestsData.map(g => [g.guestId || g.id, g])).values()
        );
        
        // Normalize - keep ALL fields for potential future use
        const list = uniqueGuests.map((g) => ({
          ...g, // Keep all original fields from v3
          id: g.id || g.guestId || g.GuestId || '',
          name: g.name || g.guestName || g.firstName && g.lastName ? `${g.firstName} ${g.lastName}` : g.firstName || '',
          room: g.room || g.roomNo || g.location || '',
          consultingDoctor: g.consultingDoctor || g.doctor || '',
          checkIn: g.checkIn || g.startDate || g.arrivalDate || '',
          checkOut: g.checkOut || g.endDate || g.departureDate || '',
        }));
        
        // Filter out guests without names and sort alphabetically by name
        const sorted = list
          .filter((g) => g.name && g.name.trim() !== '') // Only show guests with names
          .sort((a, b) => {
            const nameA = (a.name || '').toLowerCase();
            const nameB = (b.name || '').toLowerCase();
            return nameA.localeCompare(nameB);
          });
        setAllGuests(sorted); 
      } catch (e) {
        setAllGuests([]);
      } finally {
        setGuestLookupReady(true);
      }
    };
    fetchGuests();
  }, []);

  // Persist guest filter selection to localStorage
  useEffect(() => {
    localStorage.setItem('receptionDeskGuestFilter', guestFilter);
  }, [guestFilter]);

  // Filter guests based on selected filter (upcoming/inhouse)
  useEffect(() => {
    if (guestFilter === 'upcoming') {
      setGuests(allGuests.filter(g => isUpcoming(g)));
    } else if (guestFilter === 'inhouse') {
      setGuests(allGuests.filter(g => isInHouse(g)));
    } else {
      setGuests(allGuests);
    }
  }, [guestFilter, allGuests, calendarDay]);

  const todayISO = calendarDay;
  const tomorrowISO = useMemo(() => addDaysISO(calendarDay, 1), [calendarDay]);
  const next7DaysISO = useMemo(() => {
    const days = [];
    for (let i = 0; i < 7; i++) days.push(addDaysISO(calendarDay, i));
    return days;
  }, [calendarDay]);
  const dateRange = useMemo(() => {
    const range = [];
    for (let i = 0; i <= 13; i++) range.push(addDaysISO(calendarDay, i));
    return range;
  }, [calendarDay]);
  const timelineDays = useMemo(() => {
    if (dayFilter === 'today') return [todayISO];
    if (dayFilter === 'tomorrow') return [tomorrowISO];
    if (dayFilter === 'next7days') return next7DaysISO;
    return dateRange;
  }, [dayFilter, todayISO, tomorrowISO, next7DaysISO, dateRange]);

  const scheduleFocusDate = useMemo(() => {
    if (dayFilter === 'today') return todayISO;
    if (dayFilter === 'tomorrow') return tomorrowISO;
    if (date && timelineDays.includes(date)) return date;
    return timelineDays[0] || calendarDay;
  }, [dayFilter, todayISO, tomorrowISO, date, timelineDays, calendarDay]);

  const bookingGuests = useMemo(() => {
    const byKey = new Map();
    for (const d of dateRange) {
      for (const b of bookingsByDate[d] || []) {
        const id = b.guestId || b.guest_id;
        const name = b.guestName || b.guest_name;
        if (!name && !id) continue;
        const key = String(id || name);
        if (!byKey.has(key)) {
          byKey.set(key, { id: id || name, name: name || String(id), fromBooking: true });
        }
      }
    }
    return Array.from(byKey.values());
  }, [bookingsByDate, dateRange]);

  const mergedAllGuests = useMemo(() => {
    const map = new Map();
    allGuests.forEach((g) => map.set(String(g.id || g.name), g));
    bookingGuests.forEach((g) => {
      const key = String(g.id);
      if (!map.has(key)) map.set(key, g);
    });
    return Array.from(map.values()).sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
    );
  }, [allGuests, bookingGuests]);

  const guestAutocompleteOptions = useMemo(() => {
    const q = guestQuery.trim().toLowerCase();
    const tabPool = guestFilter === 'upcoming'
      ? mergedAllGuests.filter((g) => isUpcoming(g))
      : guestFilter === 'inhouse'
        ? mergedAllGuests.filter((g) => isInHouse(g))
        : mergedAllGuests;
    if (!q) return tabPool;
    return mergedAllGuests.filter((g) => {
      const name = (g.name || '').toLowerCase();
      const id = String(g.id || '').toLowerCase();
      return name.includes(q) || id.includes(q);
    });
  }, [guestQuery, guestFilter, mergedAllGuests, calendarDay]);

  const partnerAutocompleteOptions = useMemo(() => {
    const q = partnerQuery.trim().toLowerCase();
    const tabPool = guestFilter === 'upcoming'
      ? mergedAllGuests.filter((g) => isUpcoming(g))
      : guestFilter === 'inhouse'
        ? mergedAllGuests.filter((g) => isInHouse(g))
        : mergedAllGuests;
    if (!q) return tabPool;
    return mergedAllGuests.filter((g) => {
      const name = (g.name || '').toLowerCase();
      const id = String(g.id || '').toLowerCase();
      return name.includes(q) || id.includes(q);
    });
  }, [partnerQuery, guestFilter, mergedAllGuests, calendarDay]);

  const fetchMonth = useCallback(async () => {
    // Fetch data for the timeline date range
    if (!dateRange.length) return;
    try {
      setLoading(true);
      const entries = await Promise.all(
        dateRange.map(async (d) => {
          const res = await apiGet(`/api/bookings/${d}?_=${Date.now()}`);
          const data = await res.json();
          return [d, Array.isArray(data) ? data : []];
        })
      );
      const map = {};
      for (const [d, list] of entries) map[d] = list;
      setBookingsByDate(map);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchMonth();
  }, [fetchMonth]);

  const fetchRosterForRange = useCallback(async () => {
    if (!dateRange.length) {
      setRosterByDate({});
      return;
    }
    try {
      const startDate = dateRange[0];
      const endDate = dateRange[dateRange.length - 1];
      const response = await apiGet(`/api/roster/assignments?start_date=${startDate}&end_date=${endDate}`);
      if (response.ok) {
        const data = await response.json();
        setRosterByDate(data || {});
      } else {
        setRosterByDate({});
      }
    } catch (error) {
      console.error('Error fetching roster for reception desk:', error);
      setRosterByDate({});
    }
  }, [dateRange]);

  useEffect(() => {
    fetchRosterForRange();
  }, [fetchRosterForRange]);

  const fetchAvailableActivitiesForRange = useCallback(async () => {
    if (!dateRange.length || !selectedGuest) {
      setAvailableActivitiesByDate({});
      return;
    }
    
    try {
      const startDate = dateRange[0];
      const endDate = dateRange[dateRange.length - 1];
      const response = await apiGet(`/api/activities/timetable/available?start_date=${startDate}&end_date=${endDate}`);
      const data = await response.json();
      setAvailableActivitiesByDate(data);
    } catch (error) {
      console.error('Error fetching available activities:', error);
      setAvailableActivitiesByDate({});
    }
  }, [dateRange, selectedGuest]);

  useEffect(() => { 
    fetchAvailableActivitiesForRange(); 
  }, [fetchAvailableActivitiesForRange]);

  const filterFn = useCallback((b) => {
    const gq = guestQuery.trim().toLowerCase();
    const pq = partnerQuery.trim().toLowerCase();
    const rq = roomQuery.trim().toLowerCase();
    const guestName = String(b.guestName || b.guest_name || b.name || '').toLowerCase();
    const guestId = String(b.guestId || b.guest_id || b.id || '').toLowerCase();
    const roomName = String(b.room || b.room_name || '').toLowerCase();

    const matchesGuest = !gq || guestName.includes(gq) || guestId.includes(gq);
    const matchesPartner = !pq || guestName.includes(pq) || guestId.includes(pq);
    const matchesRoom = !rq || roomName.includes(rq);
    const matchesSelectedService = !selectedService || String(b.treatment || b.service || '').toLowerCase() === String(selectedService.name || selectedService).toLowerCase();
    const therapistFilter = selectedTherapist ? String(selectedTherapist.name || selectedTherapist) : '';
    const matchesSelectedTherapist = !therapistFilter || bookingIncludesTherapist(b, therapistFilter);
    const withinGuests = (gq ? matchesGuest : false) || (pq ? matchesPartner : false) || (!gq && !pq);
    return withinGuests && matchesRoom && matchesSelectedService && matchesSelectedTherapist;
  }, [guestQuery, partnerQuery, roomQuery, selectedService, selectedTherapist]);

  const filteredByDate = useMemo(() => {
    const out = {};
    for (const d of timelineDays) {
      const list = bookingsByDate[d] || [];
      out[d] = list.filter(filterFn);
    }
    return out;
  }, [bookingsByDate, timelineDays, filterFn]);

  const totalMatches = useMemo(() => {
    // Count only matches in the currently visible timeline days
    return timelineDays.reduce((acc, d) => acc + (filteredByDate[d]?.length || 0), 0);
  }, [filteredByDate, timelineDays]);

  const bookingMatchesGuestQuery = useCallback((booking, query, guest) => {
    const q = (query || '').trim().toLowerCase();
    const bookingName = String(booking.guestName || booking.guest_name || '').toLowerCase();
    const bookingId = String(booking.guestId || booking.guest_id || '').toLowerCase();

    if (guest) {
      const guestId = String(guest.id || guest.guestId || '').toLowerCase();
      const guestName = (guest.name || guest.guestName || '').toLowerCase();
      if (guestId && bookingId && guestId === bookingId) return true;
      if (guestName && bookingName && (bookingName === guestName || bookingName.includes(guestName) || guestName.includes(bookingName))) {
        return true;
      }
    }
    if (q && (bookingName.includes(q) || bookingId.includes(q))) return true;
    return false;
  }, []);

  const getBookingStart = (b) => b.startTime || b.start_time;
  const getBookingEnd = (b) => b.endTime || b.end_time;

  const answers = useMemo(() => {
    const now = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes();

    const buildSchedule = (matchFn) => {
      const items = [];
      for (const d of timelineDays) {
        for (const b of bookingsByDate[d] || []) {
          if (matchFn(b)) items.push({ ...b, date: d });
        }
      }
      return items.sort((a, b) => {
        if (a.date !== b.date) return a.date.localeCompare(b.date);
        return (parseMinutes(getBookingStart(a)) ?? 0) - (parseMinutes(getBookingStart(b)) ?? 0);
      });
    };

    const isInProgress = (b) => {
      if (b.date !== calendarDay) return false;
      const start = parseMinutes(getBookingStart(b));
      const end = parseMinutes(getBookingEnd(b));
      return start != null && end != null && nowMin >= start && nowMin < end;
    };

    const findNextBooking = (schedule) => {
      if (!schedule.length) return null;
      if (scheduleFocusDate > calendarDay) {
        return schedule.find((b) => b.date >= scheduleFocusDate) || null;
      }
      if (scheduleFocusDate === calendarDay) {
        for (const b of schedule) {
          if (b.date > calendarDay) return b;
          if (b.date < calendarDay) continue;
          const start = parseMinutes(getBookingStart(b));
          if (start != null && start > nowMin) return b;
        }
        return null;
      }
      return schedule.find((b) => b.date === scheduleFocusDate) || null;
    };

    const guestHasQuery = Boolean(guestQuery.trim() || selectedGuest);
    const guestSchedule = guestHasQuery
      ? buildSchedule((b) => bookingMatchesGuestQuery(b, guestQuery, selectedGuest))
      : [];

    const current = scheduleFocusDate === calendarDay ? (guestSchedule.find(isInProgress) || null) : null;
    const next = findNextBooking(guestSchedule);

    const partnerHasQuery = Boolean(partnerQuery.trim());
    const partnerSchedule = partnerHasQuery
      ? buildSchedule((b) => bookingMatchesGuestQuery(b, partnerQuery, null))
      : [];
    const partnerNext = findNextBooking(partnerSchedule);

    return { current, next, partnerNext };
  }, [bookingsByDate, timelineDays, calendarDay, scheduleFocusDate, guestQuery, partnerQuery, selectedGuest, bookingMatchesGuestQuery]);

  const formatQuickAnswerTime = (booking) => {
    if (!booking) return '';
    const datePrefix = booking.date && booking.date !== scheduleFocusDate
      ? `${formatLocalDayLabel(booking.date)} • `
      : '';
    return `${datePrefix}${getBookingStart(booking)}`;
  };

  const listHeader = useMemo(() => {
    const g = guestQuery.trim();
    const p = partnerQuery.trim();
    if (g && p) return `Results — ${g} & ${p}`;
    if (g) return `Results — ${g}`;
    if (p) return `Results — ${p}`;
    return 'Results';
  }, [guestQuery, partnerQuery]);

  const therapistFilterName = selectedTherapist
    ? String(selectedTherapist.name || selectedTherapist)
    : '';

  const getTherapistAvailabilityStatus = useCallback((therapistName, focusDate = date) => {
    if (!therapistName || !focusDate) {
      return { status: 'unknown', label: 'Select date', tone: 'default' };
    }

    const rostered = getRosteredTherapistNames(rosterByDate[focusDate] || {}).has(therapistName);
    if (!rostered) {
      return { status: 'off-duty', label: 'Off duty', tone: 'error' };
    }

    const now = new Date();
    const isToday = focusDate === calendarDay;
    const atMinutes = isToday ? (now.getHours() * 60 + now.getMinutes()) : 9 * 60;
    const activeBooking = getTherapistBookingAt(therapistName, focusDate, bookingsByDate, atMinutes);

    if (activeBooking) {
      const endLabel = activeBooking.endTime || activeBooking.end_time || '';
      return {
        status: 'busy',
        label: endLabel ? `Busy until ${endLabel}` : 'Busy now',
        tone: 'warning',
        booking: activeBooking,
      };
    }

    const nextBooking = (bookingsByDate[focusDate] || [])
      .filter((booking) => bookingIncludesTherapist(booking, therapistName))
      .map((booking) => ({
        booking,
        start: parseMinutes(booking.startTime || booking.start_time) ?? Number.MAX_SAFE_INTEGER,
      }))
      .filter((item) => item.start > atMinutes)
      .sort((a, b) => a.start - b.start)[0];

    if (nextBooking) {
      const startLabel = nextBooking.booking.startTime || nextBooking.booking.start_time || '';
      return {
        status: 'available',
        label: startLabel ? `Available until ${startLabel}` : 'Available',
        tone: 'success',
        booking: nextBooking.booking,
      };
    }

    return { status: 'available', label: 'Available all day', tone: 'success' };
  }, [bookingsByDate, rosterByDate, date, calendarDay]);

  const therapistAutocompleteOptions = useMemo(() => {
    return Object.values(config.therapists || {})
      .map((therapist) => therapist.name)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({
        name,
        availability: getTherapistAvailabilityStatus(name, date),
      }));
  }, [config.therapists, date, getTherapistAvailabilityStatus]);

  const isTherapistSlotAvailable = useCallback((therapistName, day, slotMinutes) => {
    if (!therapistName) return false;
    const rostered = getRosteredTherapistNames(rosterByDate[day] || {}).has(therapistName);
    if (!rostered) return false;
    const activeBooking = getTherapistBookingAt(therapistName, day, bookingsByDate, slotMinutes);
    return !activeBooking;
  }, [bookingsByDate, rosterByDate]);

  const renderTherapistChip = (therapistDisplay, booking, sx = {}) => {
    const status = getTherapistAvailabilityStatus(
      booking?.therapist || therapistDisplay,
      booking?.date || date
    );
    const chipColor = status.tone === 'success'
      ? { bg: '#dcfce7', color: '#166534', border: '#86efac' }
      : status.tone === 'warning'
        ? { bg: '#fef3c7', color: '#92400e', border: '#fcd34d' }
        : status.tone === 'error'
          ? { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5' }
          : { bg: '#f3f4f6', color: '#374151', border: '#d1d5db' };

    return (
      <Chip
        size="small"
        label={therapistDisplay}
        title={status.label}
        sx={{
          height: 22,
          fontSize: '0.72rem',
          fontWeight: 600,
          bgcolor: chipColor.bg,
          color: chipColor.color,
          border: `1px solid ${chipColor.border}`,
          ...sx,
        }}
      />
    );
  };

  // Timeline booking popover (read-only)
  const [timelineAnchor, setTimelineAnchor] = useState(null);
  const [timelineBooking, setTimelineBooking] = useState(null);
  const [calendarRefresh, setCalendarRefresh] = useState(0);
  const openEditBooking = (booking, day) => {
    setDate(day);
    setSelectedBooking({ ...booking, date: day });
    setShowEditModal(true);
  };

  const openTimelineBooking = (booking, event) => {
    setTimelineBooking(booking);
    setTimelineAnchor(event?.currentTarget || null);
  };
  const closeTimelineBooking = () => {
    setTimelineAnchor(null);
    setTimelineBooking(null);
  };
  const updateTimelineStatus = async (status) => {
    const id = timelineBooking?.bookingId || timelineBooking?.id;
    if (!id) return;
    const resp = await apiPut(`/api/bookings/${id}/status`, { status });
    if (!resp.ok) {
      const body = await resp.json().catch(() => ({}));
      window.alert(body.detail || 'Could not update the treatment status');
      return;
    }
    setTimelineBooking((current) => (current ? { ...current, status } : current));
    setBookingsByDate((current) => {
      const next = {};
      for (const [day, list] of Object.entries(current)) {
        next[day] = (list || []).map((booking) => (
          (booking.bookingId || booking.id) === id ? { ...booking, status } : booking
        ));
      }
      return next;
    });
    await fetchMonth();
    setCalendarRefresh((current) => current + 1);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2, gap: 2, bgcolor: '#f6f6f6' }}>
      <Box>
        <GoldButtonGroup variant="outlined" size="small">
          <ViewButton selected={activeView === 'desk'} onClick={() => setActiveView('desk')} startIcon={<HeadsetMicOutlinedIcon fontSize="small" />}>Desk</ViewButton>
          <ViewButton selected={activeView === 'timeline'} onClick={() => setActiveView('timeline')} startIcon={<CalendarMonthOutlinedIcon fontSize="small" />}>Timeline</ViewButton>
        </GoldButtonGroup>
      </Box>

      {activeView === 'desk' && (
        <Box sx={{ display: 'flex', flex: 1, minHeight: 0, gap: 2 }}>
          {/* Left: Filters and quick answers */}
          <Paper sx={{ width: 360, p: 2, overflow: 'auto', bgcolor: '#fff', border: '1px solid #e5e7eb', borderRadius: 2, boxShadow: 'rgba(145, 158, 171, 0.12) 0px 12px 24px -4px, rgba(145, 158, 171, 0.2) 0px 0px 2px 0px' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Reception Desk</Typography>

            {/* Guest Filter Toggle */}
            <ButtonGroup 
              fullWidth 
              variant="outlined" 
              sx={{
                mb: 1.5,
                '& .MuiButton-root': {
                  borderColor: '#E4C984',
                  color: '#666',
                  '&:hover': {
                    borderColor: '#E4C984',
                    backgroundColor: 'rgba(228, 201, 132, 0.1)',
                  }
                },
                '& .MuiButton-root.Mui-selected': {
                  backgroundColor: '#E4C984',
                  color: '#fff',
                  borderColor: '#E4C984',
                  '&:hover': {
                    backgroundColor: '#d4b874',
                  }
                }
              }}
            >
              <Button
                className={guestFilter === 'upcoming' ? 'Mui-selected' : ''}
                onClick={() => setGuestFilter('upcoming')}
              >
                Upcoming ({apiCounts.upcoming})
              </Button>
              <Button
                className={guestFilter === 'inhouse' ? 'Mui-selected' : ''}
                onClick={() => setGuestFilter('inhouse')}
              >
                In-house ({apiCounts.inHouse})
              </Button>
            </ButtonGroup>

            <Autocomplete
              options={guestAutocompleteOptions}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return option.name || option.id || '';
              }}
              value={mergedAllGuests.find(g => g.name === guestQuery || g.id === guestQuery) || null}
              onChange={(e, newValue) => {
                if (newValue) {
                  setGuestQuery(newValue.name || newValue.id || '');
                  setSelectedGuest(newValue);
                } else {
                  setGuestQuery('');
                  setSelectedGuest(null);
                }
              }}
              onInputChange={(e, newInputValue) => {
                // Allow free text input as well
                if (e && e.type === 'change') {
                  setGuestQuery(newInputValue);
                  // Try to find matching guest
                  const matchingGuest = mergedAllGuests.find((g) => {
                    const name = (g.name || '').toLowerCase();
                    const id = String(g.id || '').toLowerCase();
                    const input = newInputValue.toLowerCase();
                    return name === input || id === input || name.includes(input);
                  });
                  setSelectedGuest(matchingGuest || null);
                }
              }}
              freeSolo
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Guest name or ID (e.g. John or GU001)"
                />
              )}
              renderOption={(props, option) => {
                const dateRange = formatDateRange(option);
                return (
                  <li {...props} key={option.id || option.name}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {option.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {option.id}{option.room ? ` • Room ${option.room}` : ''}{dateRange ? ` • ${dateRange}` : ''}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              sx={{ mb: 1.5 }}
            />
            <Autocomplete
              options={partnerAutocompleteOptions}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return option.name || option.id || '';
              }}
              value={mergedAllGuests.find(g => g.name === partnerQuery || g.id === partnerQuery) || null}
              onChange={(e, newValue) => {
                if (newValue) {
                  setPartnerQuery(newValue.name || newValue.id || '');
                } else {
                  setPartnerQuery('');
                }
              }}
              onInputChange={(e, newInputValue) => {
                // Allow free text input as well
                if (e && e.type === 'change') {
                  setPartnerQuery(newInputValue);
                }
              }}
              freeSolo
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Partner name or ID (optional)"
                  placeholder="e.g. Jane or GU002"
                />
              )}
              renderOption={(props, option) => {
                const dateRange = formatDateRange(option);
                return (
                  <li {...props} key={option.id || option.name}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {option.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {option.id}{option.room ? ` • Room ${option.room}` : ''}{dateRange ? ` • ${dateRange}` : ''}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              sx={{ mb: 1.5 }}
            />
            <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            {/* Service and Therapist stacked filters */}
            <Autocomplete
              options={(config.treatments || []).map(t => t.name).sort((a, b) => a.localeCompare(b))}
              value={selectedService ? (selectedService.name || selectedService) : null}
              onChange={(e, val) => setSelectedService(val)}
              renderInput={(params) => (
                <TextField {...params} size="small" label="Service (treatment/activity)" placeholder="Select service" />
              )}
              sx={{ mb: 1.5 }}
              clearOnEscape
            />
            <Autocomplete
              options={therapistAutocompleteOptions}
              getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
              value={therapistFilterName
                ? therapistAutocompleteOptions.find((option) => option.name === therapistFilterName) || { name: therapistFilterName }
                : null}
              onChange={(e, val) => setSelectedTherapist(val?.name || val || null)}
              renderInput={(params) => (
                <TextField {...params} size="small" label="Therapist/Resource" placeholder="Select therapist" />
              )}
              renderOption={(props, option) => {
                const availability = option.availability || getTherapistAvailabilityStatus(option.name, date);
                const chipStyles = availability.tone === 'success'
                  ? { bg: '#dcfce7', color: '#166534' }
                  : availability.tone === 'warning'
                    ? { bg: '#fef3c7', color: '#92400e' }
                    : availability.tone === 'error'
                      ? { bg: '#fee2e2', color: '#991b1b' }
                      : { bg: '#f3f4f6', color: '#374151' };
                return (
                  <li {...props} key={option.name}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{option.name}</Typography>
                      <Chip
                        size="small"
                        label={availability.label}
                        sx={{
                          height: 22,
                          fontSize: '0.68rem',
                          fontWeight: 600,
                          bgcolor: chipStyles.bg,
                          color: chipStyles.color,
                        }}
                      />
                    </Box>
                  </li>
                );
              }}
              sx={{ mb: 1 }}
              clearOnEscape
            />
            {therapistFilterName && (
              <Box sx={{ mb: 2, p: 1.25, borderRadius: 1, bgcolor: '#f8fafc', border: '1px solid #e5e7eb' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                  Therapist availability on {parseLocalISO(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{therapistFilterName}</Typography>
                  {renderTherapistChip(therapistFilterName, { therapist: therapistFilterName, date })}
                </Box>
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Room (optional)"
                placeholder="Room name/number"
                value={roomQuery}
                onChange={(e) => setRoomQuery(e.target.value)}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button
                variant="contained"
                size="small"
                onClick={async () => {
                  await fetchMonth();
                  const first = dateRange.find(d => (filteredByDate[d] || []).length > 0);
                  if (first) setDate(first);
                }}
                sx={{ bgcolor: '#E4C984', '&:hover': { bgcolor: 'rgba(228, 201, 132, 0.8)' }, color: '#171717' }}
              >
                Search
              </Button>
              {selectedGuest && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ListAltIcon />}
                  onClick={() => setShowItinerary(true)}
                  sx={{
                    borderColor: '#E4C984',
                    color: '#171717',
                    '&:hover': {
                      borderColor: '#cbb16f',
                      backgroundColor: 'rgba(228, 201, 132, 0.08)'
                    }
                  }}
                >
                  Itinerary
                </Button>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ mb: 1, color: '#171717' }}>Quick answers</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1 }}>
              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'rgba(228, 201, 132, 0.5)', color: '#171717', border: '1px solid #E4C984' }}>
                <Typography variant="caption" sx={{ color: '#171717' }}>Current treatment</Typography>
                {answers.current ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      <Chip
                        size="small"
                        label={`${answers.current.startTime}–${answers.current.endTime}`}
                        sx={{ height: 22, fontSize: '0.72rem', fontWeight: 700, bgcolor: '#E4C984', color: '#171717' }}
                      />
                      <Typography variant="body2">{answers.current.treatment}</Typography>
                    </Box>
                    <Typography variant="body2">Room {answers.current.room}</Typography>
                    {renderTherapistChip(answers.current.therapist, answers.current)}
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: '#171717' }}>None right now</Typography>
                )}
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'rgba(228, 201, 132, 0.5)', color: '#171717', border: '1px solid #E4C984' }}>
                <Typography variant="caption" sx={{ color: '#171717' }}>Next treatment</Typography>
                {answers.next ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      <Chip
                        size="small"
                        label={formatQuickAnswerTime(answers.next)}
                        sx={{ height: 22, fontSize: '0.72rem', fontWeight: 700, bgcolor: '#E4C984', color: '#171717' }}
                      />
                      <Typography variant="body2">{answers.next.treatment}</Typography>
                    </Box>
                    <Typography variant="body2">Room {answers.next.room}</Typography>
                    {renderTherapistChip(answers.next.therapist, answers.next)}
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: '#171717' }}>No upcoming treatment</Typography>
                )}
              </Paper>
              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'rgba(228, 201, 132, 0.5)', color: '#171717', border: '1px solid #E4C984' }}>
                <Typography variant="caption" sx={{ color: '#171717' }}>Partner's treatment</Typography>
                {answers.partnerNext ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      <Chip
                        size="small"
                        label={formatQuickAnswerTime(answers.partnerNext)}
                        sx={{ height: 22, fontSize: '0.72rem', fontWeight: 700, bgcolor: '#E4C984', color: '#171717' }}
                      />
                      <Typography variant="body2">{answers.partnerNext.treatment}</Typography>
                    </Box>
                    <Typography variant="body2">Room {answers.partnerNext.room}</Typography>
                    {renderTherapistChip(answers.partnerNext.therapist, answers.partnerNext)}
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: '#171717' }}>No match</Typography>
                )}
              </Paper>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {loading && <Chip label="Loading" size="small" />}
              <Chip label={`${totalMatches} results`} size="small" />
            </Box>
          </Paper>

          {/* Middle: Results list grouped by day */}
          <Paper sx={{ flex: 1, minWidth: 0, p: 2, overflow: 'auto', bgcolor: '#fff', border: '1px solid #e5e7eb', borderRadius: 2, boxShadow: 'rgba(145, 158, 171, 0.12) 0px 12px 24px -4px, rgba(145, 158, 171, 0.2) 0px 0px 2px 0px' }}>
            {/* Day Filter Tabs */}
            <Box sx={{ mb: 2 }}>
              <GoldButtonGroup variant="outlined" size="small" fullWidth>
                <ViewButton 
                  selected={dayFilter === 'today'} 
                  onClick={() => {
                    setDayFilter('today');
                    setDate(todayISO);
                  }}
                >
                  Today
                </ViewButton>
                <ViewButton 
                  selected={dayFilter === 'tomorrow'} 
                  onClick={() => {
                    setDayFilter('tomorrow');
                    setDate(tomorrowISO);
                  }}
                >
                  Tomorrow
                </ViewButton>
                <ViewButton 
                  selected={dayFilter === 'next7days'} 
                  onClick={() => setDayFilter('next7days')}
                >
                  7 Days
                </ViewButton>
                <ViewButton 
                  selected={dayFilter === 'all'} 
                  onClick={() => setDayFilter('all')}
                >
                  All
                </ViewButton>
              </GoldButtonGroup>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{listHeader}</Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDisplayDateRange(timelineDays)}
              </Typography>
            </Box>
            <Divider sx={{ mb: 1 }} />
            <List dense>
              {timelineDays.map((d) => {
                const rows = filteredByDate[d] || [];
                if (rows.length === 0) return null;
                const sortedRows = [...rows].sort((a, b) => {
                  const aTime = a.startTime || a.start_time || '00:00';
                  const bTime = b.startTime || b.start_time || '00:00';
                  return aTime.localeCompare(bTime);
                });
                return (
                  <Box key={d} sx={{ mb: 1.25 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {formatLocalDayLabel(d)}
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', columnGap: 2 }}>
                    {sortedRows.map((b, idx) => {
                      const startTime = b.startTime || b.start_time;
                      return (
                      <ListItem
                        key={b.bookingId || b.id || `${d}-${startTime}-${idx}`}
                        sx={{
                          py: 0.5,
                          pr: 6,
                          '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                          cursor: 'pointer',
                        }}
                        onClick={() => openEditBooking(b, d)}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            size="small"
                            aria-label="Edit booking"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditBooking(b, d);
                            }}
                            sx={{
                              color: '#E4C984',
                              '&:hover': { bgcolor: 'rgba(228, 201, 132, 0.2)' },
                            }}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        }
                      >
                        <ListItemText
                          primary={(
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                              <Chip
                                size="small"
                                label={startTime}
                                sx={{
                                  height: 22,
                                  fontSize: '0.72rem',
                                  fontWeight: 700,
                                  bgcolor: '#E4C984',
                                  color: '#171717',
                                }}
                              />
                              <Typography component="span" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
                                {b.treatment}
                              </Typography>
                            </Box>
                          )}
                          secondary={(
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                              <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                {b.guestName || b.guest_name} • Room {b.room}
                              </Typography>
                              {(() => {
                                const therapists = b.therapists || [];
                                const therapistsCount = b.therapistsCount || 1;
                                let therapistDisplay = b.therapist;
                                if (therapistsCount > 1) {
                                  const primary = therapists.find((t) => t.is_primary);
                                  const primaryName = primary ? primary.name.split(' ')[0] : b.therapist;
                                  therapistDisplay = `${primaryName} +(${therapistsCount - 1})`;
                                }
                                return renderTherapistChip(therapistDisplay, { ...b, date: d });
                              })()}
                            </Box>
                          )}
                        />
                      </ListItem>
                    );
                    })}
                    </Box>
                  </Box>
                );
              })}
              {totalMatches === 0 && (
                <Typography variant="caption" color="text.disabled">No matching bookings in date range</Typography>
              )}
            </List>
          </Paper>

        </Box>
      )}

      {activeView === 'timeline' && (
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <Paper sx={{ height: '100%', p: 2, overflow: 'hidden', bgcolor: '#fff', border: '1px solid #e5e7eb', borderRadius: 2, boxShadow: 'rgba(145, 158, 171, 0.12) 0px 12px 24px -4px, rgba(145, 158, 171, 0.2) 0px 0px 2px 0px' }}>
            <CalendarPanel
              selectedDate={date}
              onDateChange={setDate}
              dateRange={dateRange}
              config={config}
              refreshTrigger={calendarRefresh}
              onEditBooking={(booking, e) => openTimelineBooking(booking, e)}
              headerVariant="compact"
            />
          </Paper>

          {/* Read-only popover for booking details */}
          <Popover
            open={Boolean(timelineAnchor)}
            anchorEl={timelineAnchor}
            onClose={closeTimelineBooking}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <Box sx={{ p: 2, minWidth: 280 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#171717' }}>Booking</Typography>
              {timelineBooking ? (
                <>
                  <Typography variant="body2"><strong>Time</strong>: {timelineBooking.startTime} – {timelineBooking.endTime}</Typography>
                  <Typography variant="body2"><strong>Guest</strong>: {timelineBooking.guestName}</Typography>
                  <Typography variant="body2"><strong>Treatment</strong>: {timelineBooking.treatment}</Typography>
                  <Typography variant="body2"><strong>Therapist</strong>: {timelineBooking.therapist}</Typography>
                  <Typography variant="body2"><strong>Room</strong>: {timelineBooking.room}</Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}><strong>Status</strong>: {(() => {
                    const rawStatus = String(timelineBooking.status || 'confirmed').toLowerCase();
                    const labels = {
                      available: 'Confirmed',
                      scheduled: 'Confirmed',
                      confirmed: 'Confirmed',
                      checked_in: 'Checked in',
                      in_progress: 'In progress',
                      done: 'Done',
                    };
                    return labels[rawStatus] || rawStatus.replaceAll('_', ' ');
                  })()}</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                    {[
                      ['checked_in', 'Checked in'],
                      ['in_progress', 'In progress'],
                      ['done', 'Done'],
                    ].map(([value, label]) => (
                      <Button
                        key={value}
                        size="small"
                        variant={timelineBooking.status === value ? 'contained' : 'outlined'}
                        onClick={() => updateTimelineStatus(value)}
                        sx={{
                          borderColor: '#E4C984',
                          color: timelineBooking.status === value ? '#171717' : '#666',
                          bgcolor: timelineBooking.status === value ? '#E4C984' : 'transparent',
                        }}
                      >
                        {label}
                      </Button>
                    ))}
                  </Box>
                  {timelineBooking.id && (
                    <Typography variant="caption" color="text.secondary">ID: {timelineBooking.id}</Typography>
                  )}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">No booking</Typography>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button onClick={closeTimelineBooking} size="small" variant="contained" sx={{ bgcolor: '#E4C984', color: '#171717', '&:hover': { bgcolor: 'rgba(228, 201, 132, 0.8)' } }}>Close</Button>
              </Box>
            </Box>
          </Popover>
        </Box>
      )}

      {/* Available Activities Enrollment Modal */}
      {showAvailableActivitiesModal && selectedSlotInfo && selectedGuest && (
        <Dialog 
          open={showAvailableActivitiesModal} 
          onClose={() => setShowAvailableActivitiesModal(false)} 
          fullWidth 
          maxWidth="md"
          PaperProps={{
            sx: {
              borderRadius: 2,
              overflow: 'hidden'
            }
          }}
        >
          <DialogTitle
            sx={{
              bgcolor: '#E4C984',
              color: '#171717',
              p: 2.5,
              position: 'relative'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, color: '#171717' }}>
              Available Activities
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(23,23,23,0.75)' }}>
              {selectedGuest.name} • {selectedSlotInfo.date} at {selectedSlotInfo.time}
            </Typography>
            <IconButton
              onClick={() => setShowAvailableActivitiesModal(false)}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: '#171717'
              }}
            >
              <ClearIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {selectedSlotInfo.activities.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">No activities available at this time</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
                {selectedSlotInfo.activities.map((activity) => {
                  // Calculate duration from start and end time if not provided
                  let defaultDuration = activity.duration;
                  if (!defaultDuration && activity.startTime && activity.endTime) {
                    const [sh, sm] = activity.startTime.split(':').map(Number);
                    const [eh, em] = activity.endTime.split(':').map(Number);
                    defaultDuration = (eh * 60 + em) - (sh * 60 + sm);
                  }
                  const activityDuration = activityDurations[activity.id] || defaultDuration || 60;
                  const capacityPercent = activity.maxCapacity > 0 
                    ? Math.round((activity.currentEnrollment / activity.maxCapacity) * 100) 
                    : 0;
                  
                  return (
                    <Paper
                      key={activity.id}
                      sx={{
                        p: 3,
                        border: '1px solid #e5e7eb',
                        bgcolor: '#fff',
                        borderRadius: 2,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                      }}
                    >
                      {/* Header with Activity Name and Enroll Button */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2.5 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '1.5rem' }}>
                          {activity.activityName}
                        </Typography>
                        <Button
                          variant="contained"
                          size="large"
                          onClick={async () => {
                            if (!selectedGuest) return;
                            
                            try {
                              // Calculate end time based on custom duration
                              const [startHour, startMin] = activity.startTime.split(':').map(Number);
                              const startMinutes = startHour * 60 + startMin;
                              const endMinutes = startMinutes + activityDuration;
                              const endHour = Math.floor(endMinutes / 60);
                              const endMin = endMinutes % 60;
                              const endTime = `${String(endHour).padStart(2, '0')}:${String(endMin).padStart(2, '0')}`;
                              
                              const response = await apiPost('/api/schedule/confirm', {
                                guestName: selectedGuest.name,
                                guestId: selectedGuest.id,
                                treatment: activity.activityName,
                                date: selectedSlotInfo.date,
                                startTime: activity.startTime,
                                endTime: endTime,
                                therapist: activity.instructorName,
                                room: activity.roomName,
                                activityTimetableId: activity.id,
                                generalInstruction: '',
                                treatmentInstruction: `Booked for ${activityDuration} minutes`
                              });
                              
                              if (!response.ok) {
                                const errorData = await response.json();
                                throw new Error(errorData.detail || 'Failed to enroll in activity');
                              }
                              
                              alert(`✅ ${selectedGuest.name} enrolled in ${activity.activityName} for ${activityDuration} minutes!`);
                              
                              // Refresh data
                              await fetchAvailableActivitiesForRange();
                              await fetchMonth();
                              setShowAvailableActivitiesModal(false);
                              setActivityDurations({}); // Clear durations
                            } catch (error) {
                              console.error('Error enrolling in activity:', error);
                              alert(`❌ Error: ${error.message}`);
                            }
                          }}
                          sx={{
                            bgcolor: '#E4C984',
                            color: '#171717',
                            '&:hover': {
                              bgcolor: '#d4b974'
                            },
                            minWidth: 140,
                            height: 'fit-content',
                            px: 3,
                            py: 1.5,
                            textTransform: 'uppercase',
                            fontWeight: 600,
                            fontSize: '0.95rem'
                          }}
                        >
                          ENROLL
                        </Button>
                      </Box>

                      {/* Activity Details - 2 Column Layout */}
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, mb: 3 }}>
                        {/* Left Column - Time, Instructor, Location */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {/* Time */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <AccessTimeIcon sx={{ fontSize: 20, color: '#E4C984' }} />
                            <Box>
                              <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                                TIME
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {activity.startTime} - {activity.endTime} ({defaultDuration} minutes)
                              </Typography>
                            </Box>
                          </Box>

                          {/* Instructor */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <PersonIcon sx={{ fontSize: 20, color: '#E4C984' }} />
                            <Box>
                              <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                                INSTRUCTOR
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {activity.instructorName}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Location */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <LocationOnIcon sx={{ fontSize: 20, color: '#E4C984' }} />
                            <Box>
                              <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                                LOCATION
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {activity.roomName}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                        {/* Right Column - Enrolled and Booking Duration */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {/* Enrolled */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <PeopleIcon sx={{ fontSize: 20, color: '#E4C984' }} />
                            <Box>
                              <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', display: 'block', mb: 0.25 }}>
                                ENROLLED
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                  {activity.currentEnrollment}/{activity.maxCapacity}
                                </Typography>
                                {capacityPercent > 70 && (
                                  <Chip 
                                    label="HOT" 
                                    size="small"
                                    sx={{ 
                                      bgcolor: '#ef4444',
                                      color: '#fff',
                                      fontWeight: 700,
                                      fontSize: '0.7rem',
                                      height: 20,
                                      '& .MuiChip-label': {
                                        px: 1
                                      }
                                    }}
                                  />
                                )}
                              </Box>
                            </Box>
                          </Box>

                          {/* Booking Duration */}
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            <AccessTimeIcon sx={{ fontSize: 20, color: '#E4C984', mt: 0.5 }} />
                            <Box>
                              <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                                BOOKING DURATION (MINUTES)
                              </Typography>
                              <TextField
                                type="number"
                                size="small"
                                value={activityDuration}
                                onChange={(e) => {
                                  const newDuration = parseInt(e.target.value) || 0;
                                  setActivityDurations(prev => ({
                                    ...prev,
                                    [activity.id]: newDuration
                                  }));
                                }}
                                inputProps={{ min: 15, max: defaultDuration, step: 15 }}
                                sx={{ width: 100, mb: 0.5 }}
                              />
                              <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.75rem' }}>
                                You can book up to <strong>{defaultDuration}</strong> minutes
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button 
              onClick={() => setShowAvailableActivitiesModal(false)}
              variant="outlined"
              size="large"
              sx={{
                borderColor: '#d1d5db',
                color: '#6b7280',
                textTransform: 'uppercase',
                fontWeight: 600,
                px: 4,
                py: 1.5,
                '&:hover': {
                  borderColor: '#9ca3af',
                  bgcolor: '#f9fafb'
                }
              }}
            >
              CLOSE
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Itinerary Modal */}
      <ItineraryModal
        open={showItinerary}
        onClose={() => setShowItinerary(false)}
        guestId={selectedGuest?.id || selectedGuest?.guestId}
        guestName={selectedGuest?.name || selectedGuest?.guestName}
        bookingId={selectedGuest?.bookingId || selectedGuest?.booking_id}
      />

      {/* Edit Booking Modal */}
      {showEditModal && selectedBooking && (
        <EditBookingModal
          show={showEditModal}
          booking={selectedBooking}
          config={config}
          onClose={() => { 
            setShowEditModal(false); 
            setSelectedBooking(null); 
          }}
          onDelete={async (b) => {
            const bookingId = b.id || b.bookingId;
            const bookingDate = b.date;
            if (!bookingId || !bookingDate) {
              alert('Missing booking information');
              return;
            }
            if (!window.confirm(`Delete appointment for ${b.guestName || b.guest_name}?`)) return;
            
            try {
              const resp = await apiDelete(`/api/bookings/${bookingDate}/${bookingId}`);
              if (resp.ok) {
                alert('Appointment deleted');
                setShowEditModal(false);
                setSelectedBooking(null);
                await fetchMonth(); // Refresh data
              } else {
                const j = await resp.json().catch(() => ({}));
                alert(j.detail || 'Failed to delete');
              }
            } catch (err) {
              alert('Error deleting appointment');
            }
          }}
          onSchedulingComplete={async (result) => {
            if (result?.success) {
              setShowEditModal(false);
              setSelectedBooking(null);
              await fetchMonth(); // Refresh data
            }
          }}
        />
      )}
    </Box>
  );
};

export default ReceptionDesk;


